import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Button } from "~/components/ui";
import { buildLocalizedPath, type Lang } from "~/config/route-config";

import { ErrorSurface } from "./ErrorSurface";

/**
 * 404 — the user followed a stale or wrong URL.
 *
 * Mounted by the catch-all route and by the root `ErrorBoundary` when the
 * thrown response carries status 404. The `404` status code itself is wired by
 * T38; this component is the visual layer only.
 */
export function ErrorPage404({ standalone = false }: { standalone?: boolean }) {
  const { t, i18n } = useTranslation("error");
  const lang: Lang = i18n.language === "en" ? "en" : "es";

  return (
    <ErrorSurface
      standalone={standalone}
      mark={<span className="es-glyph">404</span>}
      eyebrow={<div className="af-eyebrow">{t("notFound.eyebrow")}</div>}
      title={t("notFound.title")}
      sub={t("notFound.sub")}
      primary={
        // An <a>, not a handler: middle-click and open-in-new-tab must work.
        <Button href={buildLocalizedPath("home", lang)} variant="dark" className="w-full">
          <span>{t("notFound.primary")}</span>
          <span className="af-submit-arrow" aria-hidden="true">
            →
          </span>
        </Button>
      }
      secondary={
        <Button
          variant="text"

          onClick={() => window.history.back()}
        >
          ← {t("notFound.secondary")}
        </Button>
      }
      footer={
        <div className="row">
          <span>{t("common.brand")}</span>
          <span className="sep" aria-hidden="true" />
          {/* Q1: the canon's `/contacto` literal is a redirect stub — `support`
              is the real public route. T25 widens that surface, it does not gate it. */}
          <Link to={buildLocalizedPath("support", lang)}>{t("notFound.report")}</Link>
        </div>
      }
    />
  );
}
